import PersonTypeRadio from 'components/shared/RadioButtons/PersonTypeRadio'
import { useIntl } from 'react-intl'
import InfoTooltip from 'components/shared/InfoTooltip'
import Text from 'components/shared/Text'
import Box from 'components/shared/Box'
import { CARD_REGULATIONS_LINK } from 'constants/links'
import {
    CardPersonDto,
    useListCreatorCardPersonsQuery,
} from 'store/api-slice/hooks'
import { useApp } from 'store/steps'
import Loader from 'components/shared/Loader'
import { CardPerson } from 'store/api-slice/types'
import { DefaultTheme } from 'styled-components'
import { useMemo } from 'react'
import { calculateComputedStyles } from 'helpers/calculateInlineStyles'
import { useResolutions } from 'store/resolutions'

interface PersonTypePickerProps {
    index: number
    title: string
    setPerson: (personDetail: CardPersonDto) => void
    selectedPerson?: CardPerson
    theme?: DefaultTheme
    testIdPrefix?: string
}

const PersonTypePicker = ({
    index,
    title,
    selectedPerson,
    setPerson,
    theme,
    testIdPrefix,
}: PersonTypePickerProps) => {
    const { formatMessage } = useIntl()
    const { language } = useApp()
    const { isMobile } = useResolutions()

    const { data, isFetching } = useListCreatorCardPersonsQuery({
        _locale: language,
    })

    const personOptions = useMemo(
        () =>
            data?.persons?.map((person) => {
                const { key, description, title, displayName } = person

                if (!key || !title) return undefined

                return (
                    <PersonTypeRadio
                        key={`${index}${key}`}
                        option={key}
                        label={title}
                        info={description}
                        isChecked={selectedPerson === key}
                        id={key}
                        onClick={() =>
                            setPerson({
                                // TODO: ask backend to unify types
                                key: key as CardPerson,
                                description,
                                title,
                                displayName,
                            })
                        }
                        theme={theme}
                        testId={
                            testIdPrefix
                                ? `${testIdPrefix}-${index}-select-person-${key}`
                                : undefined
                        }
                    />
                )
            }),
        [data, selectedPerson, index]
    )

    return (
        <Box isFullWidth>
            <Box
                id={`cardPersonType${index}`}
                direction="row"
                align="center"
                gap={8}
                mb={[16, 24]}
            >
                <Text variant="heading3" color="grayscale_900" theme={theme}>
                    {title}
                </Text>

                <InfoTooltip
                    mobilePlacement="bottom"
                    offsetSizes={calculateComputedStyles(`voucher-${index}`)}
                    isLarge={!isMobile}
                    message={formatMessage(
                        {
                            id: 'annual.config.person_type_tooltip',
                            defaultMessage:
                                'Podczas pierwszej wizyty w parku sprawdzimy, czy podane dane zgadzają się z Twoim dowodem tożsamości. Uprawnienia do zniżki muszą być ważne w dniu aktywacji karty. Szczegóły znajdziesz w <a>Regulaminie kart całorocznych.</a>',
                            description:
                                'Keep the html tags in the translation',
                        },
                        {
                            // eslint-disable-next-line id-length
                            a: (chunks: string[]) => (
                                <Text
                                    as="a"
                                    variant="bodySmall"
                                    color="grayscale_0"
                                    isUnderlined
                                    href={CARD_REGULATIONS_LINK}
                                    target="_blank"
                                >
                                    {chunks}
                                </Text>
                            ),
                        }
                    )}
                    isInteractive
                />
            </Box>
            <Box isFullWidth direction={['column', 'row']} gap={[8, 16]}>
                {isFetching && <Loader />}
                {!isFetching && personOptions}
            </Box>
        </Box>
    )
}
export default PersonTypePicker
